import { useMemo, useState } from 'react';
import { ClassNames, css } from '@emotion/react';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import type { RowProps } from 'shared/components/nv-table';
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvSwitch from 'shared/components/inputs/nv-switch';
import MoreOptionsTarget from 'athena/components/more-options-target';
import { gray7 } from 'styles/global_defaults/colors';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { RootState } from 'redux/schemas';
import {
  ActionItemRowHandlers,
  SavedActionItem,
  buildAssigneeRoleOrder,
} from '../types';
import { assigneeCellStyles } from './styles';

type ActionItemReadRowProps = RowProps<SavedActionItem> & Pick<ActionItemRowHandlers,
  'setEditingId' | 'onSave' | 'onDelete'>;

const rowStyles = css`
  min-height: 65px;
  margin-bottom: ${standardSpacing}px;
  &:hover {
    background-color: ${gray7};
    .options-trigger { opacity: 1; }
  }
`;

const optionsCellStyles = css`
  .options-trigger { opacity: 0; }
  .options-dropdown:focus .options-trigger { opacity: 1; }
`;

const ActionItemReadRow = ({
  row, className, getCellStyle, setEditingId, onSave, onDelete,
}: ActionItemReadRowProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.ACTION_ITEMS;
  const dataQATag = config.pendo.athena.mentorshipProgram.preAssignedActionItems;
  const { id, description, assignedRole, required } = row.original;

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const aliases = useSelector((state: RootState) => getMentoringProgramAliases(state, mentoringProgram?.id ?? 0));
  const mentorsAlias = aliases?.MentorsAlias ?? '';
  const menteesAlias = aliases?.MenteesAlias ?? '';
  const assigneeRoleOrder = buildAssigneeRoleOrder(mentorsAlias, menteesAlias);

  const readAssigneeOptions: NvDropdownOption[] = useMemo(() => (
    assigneeRoleOrder.map(({ role, text }) => ({
      type: 'text' as const,
      text,
      callback: () => onSave({ description, assignedRole: role, required }, id),
    }))
  ), [mentorsAlias, menteesAlias, description, required, onSave, id]);

  const deleteOptions: NvDropdownOption[] = useMemo(() => [
    {
      type: 'text',
      text: translationBase.DELETE(),
      textClassName: 'color-danger',
      callback: () => onDelete(id),
      dataQa: dataQATag.optionsMenuDropdown.delete,
    },
  ], [translationBase, onDelete, id, dataQATag]);

  const readAssigneeTitle = assigneeRoleOrder.find(o => o.role === assignedRole)?.text ?? translationBase.PLACEHOLDER.CHOOSE_ASSIGNEE();
  const readSelectedAssigneeIndex = assigneeRoleOrder.findIndex(o => o.role === assignedRole);

  return (
    <ClassNames>
      {({ css: classNamesCss }) => (
        <div
          css={rowStyles}
          className={`${className} align-items-center border-bottom border-gray-6`}
          data-qa={`${dataQATag.actionItemRow}_${id}`}
        >
          <button
            type='button'
            className={`${classNamesCss`${getCellStyle(0)}`} text-large-body d-flex align-items-center text-left color-gray-1 bg-transparent border-0 h-100`}
            onClick={() => setEditingId(id)}
          >
            {description}
          </button>
          <div css={assigneeCellStyles} className={classNamesCss`${getCellStyle(1)}`}>
            <NvDropdown
              showRequiredIndicator
              items={readAssigneeOptions}
              title={readAssigneeTitle}
              buttonStyle={NvDropdownButtonStyle.FORM_SMALL}
              showSelectedIndicator
              initialIndex={readSelectedAssigneeIndex}
              toggleDataQa={dataQATag.assigneeDropdown.toggle}
            />
          </div>
          <div className={classNamesCss`${getCellStyle(2)}`}>
            <NvSwitch
              checked={required}
              onChange={(checked) => onSave({ description, assignedRole, required: checked }, id)}
              ariaLabel={translationBase.ARIA_LABEL.REQUIRED_TOGGLE()}
              dataQa={dataQATag.requiredToggle}
            />
          </div>
          <div
            css={optionsCellStyles}
            className={`${classNamesCss`${getCellStyle(3)}`} d-flex align-items-center justify-content-end`}
            onClick={(e) => e.stopPropagation()}
          >
            <NvDropdown
              items={deleteOptions}
              align={NvDropdownAlign.RIGHT}
              buttonStyle={NvDropdownButtonStyle.CUSTOM}
              customTargetClass='options-dropdown justify-content-end'
              customTarget={() => <MoreOptionsTarget className='options-trigger' />}
              onToggle={setIsDropdownOpen}
              ariaExpanded={isDropdownOpen}
              ariaRole='menu'
              altLabel={translationBase.ARIA_LABEL.OPTIONS()}
              toggleDataQa={dataQATag.optionsMenuDropdown.toggle}
            />
          </div>
        </div>
      )}
    </ClassNames>
  );
};

export default ActionItemReadRow;
